import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import CustomModal from "../../../packages/CustomModal";
import WPTertiaryButton from "../../../packages/WPTertiaryButton";

import AdvancedSettingsInfo from "./AdvancedSettingsInfo/AdvancedSettingsInfo";
import {
  cipAndShutdownStaticConfig,
  CIPPredictionRecommendationConfig,
  cleaningEffectivenessConfig,
  lifetimePredictionConfig,
  outlierConfig,
  predictPermeabilityConfig,
} from "./AdvancedSettings.config";
import { ADVANCED_SETTINGS_DEFAULTS } from "./advancedSettings.constants";
import CIPPredictionRecommendation from "./CIPPredictionRecommendation";
import CIPShutdownIdentification from "./CIPShutdownIdentification";
import CleaningEffectivenessCalculation from "./CleaningEffectivenessCalculation";
import LifetimePrediction from "./LifetimePrediction";
import OutlierRemoval from "./OutlierRemoval";
import useAdvancedSettings from "./useAdvancedSettings";

import { RedoIcon } from "../../../assets/icons";

import "./AdvancedSettings.scss";

const AdvancedSettings = ({
  handleClose,
  selectedTrain,
  cipPredictionAndRecommendation = false,
  lifetime = false,
  onSave = null,
  outlierRemoval = false,
  cipAndShutdownIdentification = false,
  cleaningEffectivenessCalculation = false,
  excludeFields = [],
}) => {
  const { t } = useTranslation();

  const {
    pressure,
    cipDynamicConfig,
    defaultSettings,
    fetchSettings,
    saveSettings,
    buildDefaultChecked,
  } = useAdvancedSettings({ selectedTrain });

  const [settings, setSettings] = useState(defaultSettings);
  const [initialSettings, setInitialSettings] = useState(defaultSettings);
  const [suggestedSettings, setSuggestedSettings] = useState({});
  const [errors, setErrors] = useState({});
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [showOverlayLoader, setShowOverlayLoader] = useState(false);
  const [resetKey, setResetKey] = useState(0);

  const [checkedPressureDropOptions, setCheckedPressureDropOptions] = useState(
    () => buildDefaultChecked(cipDynamicConfig),
  );
  const [
    initialCheckedPressureDropOptions,
    setInitialCheckedPressureDropOptions,
  ] = useState(() => buildDefaultChecked(cipDynamicConfig));

  const modalDefaultSettings = useMemo(() => {
    const absoluteDefault =
      cipDynamicConfig?.pressure_drop_threshold?.options?.absolute
        ?.defaultValue;

    return {
      ...ADVANCED_SETTINGS_DEFAULTS,
      ...(absoluteDefault != null && {
        pressure_drop_abs_threshold: absoluteDefault,
        pressure_drop_abs_threshold_predict: absoluteDefault,
      }),
    };
  }, [cipDynamicConfig]);

  const activeRestoreKeys = useMemo(() => {
    const keys = new Set();

    if (outlierRemoval) {
      Object.keys(outlierConfig).forEach((key) => keys.add(key));
    }

    if (cipAndShutdownIdentification) {
      Object.values(cipDynamicConfig.pressure_drop_threshold.options).forEach(
        (option) => keys.add(option.settingsKey),
      );
      Object.keys(cipAndShutdownStaticConfig).forEach((key) => keys.add(key));
    }

    if (cleaningEffectivenessCalculation) {
      Object.keys(
        cleaningEffectivenessConfig.cleaningEffectivenessCalculation,
      ).forEach((key) => keys.add(key));
    }

    if (cipPredictionAndRecommendation) {
      Object.entries(
        CIPPredictionRecommendationConfig.cipPredictionAndRecommendation,
      )
        .filter(([key]) => !excludeFields.includes(key))
        .forEach(([key, config]) => {
          keys.add(key);

          if (config.threshold?.settingsKey) {
            keys.add(config.threshold.settingsKey);
          }

          if (config.checkboxThresholds) {
            config.checkboxThresholds.forEach((thresholdConfig) => {
              keys.add(thresholdConfig.checkboxKey);
              keys.add(thresholdConfig.settingsKey);
            });
          }
        });

      if (!lifetime) {
        Object.keys(predictPermeabilityConfig).forEach((key) => keys.add(key));
      }
    }

    if (lifetime) {
      Object.keys(lifetimePredictionConfig).forEach((key) => keys.add(key));
    }

    return Array.from(keys);
  }, [
    outlierRemoval,
    cipAndShutdownIdentification,
    cleaningEffectivenessCalculation,
    cipPredictionAndRecommendation,
    lifetime,
    excludeFields,
    cipDynamicConfig,
  ]);

  const load = async () => {
    const result = await fetchSettings(setShowOverlayLoader);
    if (result) {
      const { transformed, checkedFromFlag, suggestedData } = result;
      setSettings(transformed);
      setInitialSettings(transformed);
      setCheckedPressureDropOptions(checkedFromFlag);
      setInitialCheckedPressureDropOptions(checkedFromFlag);
      setSuggestedSettings({ ...suggestedData });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const allTrackedKeys = useMemo(() => {
    const cipDynamicSettingsKeys = Object.values(
      cipDynamicConfig.pressure_drop_threshold.options,
    ).map((o) => o.settingsKey);
    const cipStaticSettingsKeys = Object.keys(cipAndShutdownStaticConfig);
    const cleaningEffectivenessKeys = Object.keys(
      cleaningEffectivenessConfig.cleaningEffectivenessCalculation,
    );
    const cipPredictionKeys = Object.keys(
      CIPPredictionRecommendationConfig.cipPredictionAndRecommendation,
    );
    const cipPredictionThresholdKeys = Object.values(
      CIPPredictionRecommendationConfig.cipPredictionAndRecommendation,
    )
      .filter((c) => c.threshold)
      .map((c) => c.threshold.settingsKey);
    const cipPredictionCheckboxKeys = Object.values(
      CIPPredictionRecommendationConfig.cipPredictionAndRecommendation,
    )
      .filter((c) => c.checkboxThresholds)
      .flatMap((c) =>
        c.checkboxThresholds.flatMap((ct) => [ct.checkboxKey, ct.settingsKey]),
      );
    const lifetimePredictionKeys = Object.keys(lifetimePredictionConfig);
    const predictPermeabilityKeys = lifetime
      ? []
      : Object.keys(predictPermeabilityConfig);
    return [
      ...Object.keys(outlierConfig),
      ...cipDynamicSettingsKeys,
      ...cipStaticSettingsKeys,
      ...cleaningEffectivenessKeys,
      ...cipPredictionKeys,
      ...cipPredictionThresholdKeys,
      ...cipPredictionCheckboxKeys,
      ...predictPermeabilityKeys,
      ...lifetimePredictionKeys,
    ];
  }, [cipDynamicConfig, lifetime]);

  useEffect(() => {
    const hasCheckboxChanged = Object.keys(checkedPressureDropOptions).some(
      (k) =>
        checkedPressureDropOptions[k] !== initialCheckedPressureDropOptions[k],
    );
    const atLeastOneChecked = Object.values(checkedPressureDropOptions).some(
      Boolean,
    );
    const hasChanged =
      hasCheckboxChanged ||
      allTrackedKeys.some((key) => settings[key] !== initialSettings[key]);
    const hasErrors = Object.values(errors).some((e) => e !== null);
    setIsSaveDisabled(!hasChanged || hasErrors || !atLeastOneChecked);
  }, [
    settings,
    initialSettings,
    errors,
    checkedPressureDropOptions,
    initialCheckedPressureDropOptions,
    allTrackedKeys,
  ]);

  const handleChange = ({ type, key, value, min, max }) => {
    if (type === "checkbox") {
      setCheckedPressureDropOptions((prev) => ({
        ...prev,
        [key]: !prev[key],
      }));
      return;
    }
    if (type === "radio") {
      setSettings((prev) => ({ ...prev, [key]: value }));
      return;
    }
    let errorMessage = null;
    let settingsUpdate = { [key]: value };

    if (key === "stabilization_days") {
      const minTime = parseFloat(settings.min_time);
      const maxTime = parseFloat(settings.max_time);
      if (
        value === "" ||
        parseFloat(value) < minTime ||
        parseFloat(value) > maxTime
      ) {
        errorMessage = `${t("advanceSettings.Rangemsg")} ${minTime} ${t("advanceSettings.And")} ${maxTime}`;
      }
    } else if (
      value === "" ||
      parseFloat(value) < min ||
      parseFloat(value) > max
    ) {
      errorMessage = `${t("advanceSettings.Rangemsg")} ${min} ${t("advanceSettings.And")} ${max}`;
    }

    const updatedErrors = { ...errors, [key]: errorMessage };

    if (key === "min_time" || key === "max_time") {
      const minTime = parseFloat(
        key === "min_time" ? value : settings.min_time,
      );
      const maxTime = parseFloat(
        key === "max_time" ? value : settings.max_time,
      );
      const stabDays = parseFloat(settings.stabilization_days);
      if (!isNaN(minTime) && !isNaN(maxTime) && !isNaN(stabDays)) {
        if (stabDays < minTime || stabDays > maxTime) {
          updatedErrors.stabilization_days = `${t("advanceSettings.Rangemsg")} ${minTime} ${t("advanceSettings.And")} ${maxTime}`;
        } else {
          updatedErrors.stabilization_days = null;
        }
      } else {
        updatedErrors.stabilization_days = null;
      }
    }

    setErrors(updatedErrors);
    setSettings((prev) => ({ ...prev, ...settingsUpdate }));
  };

  const handleSaveSettings = async () => {
    const success = await saveSettings(
      settings,
      checkedPressureDropOptions,
      setShowOverlayLoader,
    );
    if (success) {
      onSave?.();
    }
    handleClose();
  };

  const handleRestoreDefaults = () => {
    setSettings((prev) => {
      const next = { ...prev };
      activeRestoreKeys.forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(modalDefaultSettings, key)) {
          next[key] = modalDefaultSettings[key];
        }
      });
      return next;
    });

    if (cipAndShutdownIdentification) {
      const defaultChecked = buildDefaultChecked(cipDynamicConfig);
      setCheckedPressureDropOptions(defaultChecked);
      setInitialCheckedPressureDropOptions(defaultChecked);
    }

    setErrors({});
    setResetKey((prev) => prev + 1);
  };

  const onModalClose = ({ target }) => {
    if (target.id === "modal-confirm-btn") {
      handleSaveSettings();
    } else handleClose();
  };

  const restoreDefaultsFooter = (
    <WPTertiaryButton
      icon={<RedoIcon />}
      label={t("advanceSettings.restoredefaults")}
      onClick={handleRestoreDefaults}
      iconPlacement="before"
    />
  );

  const hasOutlierConfig =
    Object.keys(outlierConfig).length > 0 && outlierRemoval;

  return (
    <>
      <CustomModal
        header={t("analyseData.settings")}
        onModalClose={onModalClose}
        cancelBtn={t("ValueStrings.cancel")}
        confirmBtn={t("ValueStrings.save")}
        restoreDefaultsBtn={t("advanceSettings.restoredefaults")}
        modalFooter={restoreDefaultsFooter}
        confirmBtnDisabled={isSaveDisabled}
        width="822px"
        isLoading={showOverlayLoader}
        custombodyclass={"modal-body-container-advance"}
      >
        <AdvancedSettingsInfo />
        {hasOutlierConfig && (
          <OutlierRemoval
            suggestedSettings={suggestedSettings}
            resetKey={resetKey}
            settings={settings}
            defaultSettings={defaultSettings}
            handleChange={handleChange}
          />
        )}
        {cipAndShutdownIdentification && (
          <CIPShutdownIdentification
            cipDynamicConfig={cipDynamicConfig}
            pressure={pressure}
            suggestedSettings={suggestedSettings}
            checkedPressureDropOptions={checkedPressureDropOptions}
            resetKey={resetKey}
            settings={settings}
            defaultSettings={defaultSettings}
            handleChange={handleChange}
          />
        )}
        {cleaningEffectivenessCalculation && (
          <CleaningEffectivenessCalculation
            settings={settings}
            suggestedSettings={suggestedSettings}
            handleChange={handleChange}
            resetKey={resetKey}
          />
        )}
        {cipPredictionAndRecommendation && (
          <CIPPredictionRecommendation
            settings={settings}
            suggestedSettings={suggestedSettings}
            handleChange={handleChange}
            resetKey={resetKey}
            defaultSettings={defaultSettings}
            cipDynamicConfig={cipDynamicConfig}
            pressure={pressure}
            excludeFields={excludeFields}
            showPredictPermeabilitySection={!lifetime}
          />
        )}
        {lifetime && (
          <LifetimePrediction
            settings={settings}
            suggestedSettings={suggestedSettings}
            handleChange={handleChange}
            resetKey={resetKey}
            defaultSettings={defaultSettings}
          />
        )}
      </CustomModal>
    </>
  );
};

export default AdvancedSettings;
